import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaCalendarAlt, FaMapMarkerAlt, FaArrowRight, FaFileDownload } from 'react-icons/fa';
import { upcomingEvents } from '../data/events';
import Seo from '../components/Seo';

// Latest club news shown on the home page - update these when a new newsletter goes out
const latestNews = {
  label: 'October 2026 Newsletter',
  items: [
    {
      title: 'Moving to the New Pool',
      text: "Training is hoped to move to the new pool before the end of the year, and membership fees rise to £30 per month from 1st January 2027.",
      link: '/events#club-news'
    },
    {
      title: 'Volunteers Needed',
      text: 'We are looking for Team Managers and a Tadpoles/Mavis Wallis Coordinator to help as the club grows.',
      link: '/events#volunteers'
    },
    {
      title: 'New Pool Construction Update',
      text: 'The latest progress report on the new Marple Community Hub from Stockport Council.',
      link: '/new-pool'
    }
  ]
};

function Home() {

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <div className="min-h-screen">
      <Seo
        description="Marple Swimming Club offers competitive training and learn-to-swim lessons for all ages in Marple, Stockport. Join our friendly, community swimming club today."
        path="/"
      />
      {/* Hero */}
      <section className="relative bg-navy-900 text-white overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-navy-900 via-navy-800 to-navy-950" />
        <div className="absolute -right-32 -top-32 w-96 h-96 bg-primary-600/20 rounded-full blur-3xl" />
        <div className="absolute -left-24 bottom-0 w-72 h-72 bg-primary-600/10 rounded-full blur-3xl" />

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32 text-center">
          <motion.p
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="uppercase tracking-[0.2em] text-primary-400 font-semibold mb-5 text-sm"
          >
            Est. 1933 &middot; Marple, Stockport
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight"
          >
            Making Waves in Our Community Since 1933
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg sm:text-xl text-navy-100 max-w-2xl mx-auto mb-10 leading-relaxed"
          >
            Competitive training and learn-to-swim lessons for every age and ability &mdash;
            in a welcoming, inclusive club at the heart of Marple.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex flex-wrap gap-4 justify-center"
          >
            <Link
              to="/contact"
              className="bg-primary-600 hover:bg-primary-700 px-8 py-3.5 rounded-full font-bold shadow-lg transition-all duration-300 hover:scale-105"
            >
              Join The Club
            </Link>
            <Link
              to="/programs"
              className="bg-white/10 hover:bg-white/20 border border-white/30 px-8 py-3.5 rounded-full font-bold transition-all duration-300"
            >
              View Programs
            </Link>
          </motion.div>
        </div>

        {/* Wave divider into next section */}
        <svg className="relative block w-full text-gray-50" viewBox="0 0 1440 80" preserveAspectRatio="none" style={{ height: '60px' }}>
          <path fill="currentColor" d="M0,40 C240,90 480,0 720,20 C960,40 1200,90 1440,40 L1440,80 L0,80 Z" />
        </svg>
      </section>

      {/* Latest News */}
      <section className="bg-gray-50 pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-center text-primary-600 font-semibold uppercase tracking-widest text-sm mb-2">
              {latestNews.label}
            </p>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-navy-900 text-center mb-12">
              Latest News
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {latestNews.items.map((item, index) => (
                <Link
                  key={index}
                  to={item.link}
                  className="group bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border-l-4 border-navy-900"
                >
                  <h3 className="text-lg font-bold text-navy-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-gray-700 leading-relaxed mb-4">{item.text}</p>
                  <span className="inline-flex items-center gap-2 text-primary-600 font-semibold text-sm group-hover:text-primary-700">
                    Read more <FaArrowRight className="w-3 h-3" />
                  </span>
                </Link>
              ))}
            </div>
            <div className="text-center mt-10">
              <a
                href="/newsletter.jpg"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-primary-600 text-white px-6 py-3 rounded-full font-bold hover:bg-primary-700 transition-all duration-300 shadow-md"
              >
                <FaFileDownload />
                View Full Newsletter
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Upcoming Events */}
      <section className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-center text-primary-600 font-semibold uppercase tracking-widest text-sm mb-2">
              What's On
            </p>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-navy-900 text-center mb-12">
              Upcoming Events
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {upcomingEvents.slice(0, 3).map((event, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border-t-4 ${
                    event.type === 'social' ? 'border-green-600' : 'border-primary-600'
                  }`}
                >
                  <h3 className={`text-lg font-bold mb-3 ${
                    event.type === 'social' ? 'text-green-700' : 'text-primary-600'
                  }`}>
                    {event.title}
                  </h3>
                  <div className="space-y-1.5 text-sm">
                    <p className="flex items-center gap-2 font-semibold text-navy-900">
                      <FaCalendarAlt className={event.type === 'social' ? 'text-green-600' : 'text-primary-600'} />
                      {event.date}
                    </p>
                    <p className="flex items-center gap-2 text-gray-600">
                      <FaMapMarkerAlt className={event.type === 'social' ? 'text-green-600' : 'text-primary-600'} />
                      {event.location}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
            <div className="text-center mt-10">
              <Link
                to="/events"
                className="inline-flex items-center gap-2 text-primary-600 font-semibold hover:text-primary-700 transition-colors"
              >
                View All Events <FaArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* New Pool Section */}
      <section className="bg-white py-16 border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-center text-primary-600 font-semibold uppercase tracking-widest text-sm mb-2">
              Looking Ahead
            </p>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-navy-900 text-center mb-12">
              New Marple Community Hub Coming Soon
            </h2>

            {/* First Row - Interior */}
            <div className="grid md:grid-cols-2 gap-8 lg:gap-12 items-center mb-16">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="order-2 md:order-1"
              >
                <img
                  src="/new-pool-interior.jpg"
                  alt="New Pool Interior Design"
                  className="w-full rounded-2xl shadow-xl transform hover:scale-[1.02] transition-transform duration-300"
                />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="order-1 md:order-2"
              >
                <h3 className="font-display text-2xl font-bold text-navy-900 mb-5">
                  State-of-the-Art Swimming Facility
                </h3>
                <p className="text-gray-700 mb-4 leading-relaxed">
                  Stockport Council has secured £20 million funding for the flagship new Marple Community Hub,
                  which will feature a modern swimming pool and community facilities.
                </p>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  This exciting development will provide Marple Swimming Club with a permanent,
                  purpose-built home featuring contemporary design and excellent facilities for all our programs.
                </p>
                <a
                  href="https://www.stockport.gov.uk/news/green-light-for-flagship-new-marple-community-hub-as-council-secures-gbp20m"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block bg-primary-600 text-white px-8 py-3 rounded-full font-bold hover:bg-primary-700 transform hover:scale-105 transition-all duration-300 shadow-md"
                >
                  Read Full Announcement
                </a>
              </motion.div>
            </div>

            {/* Second Row - Exterior */}
            <div className="grid md:grid-cols-2 gap-8 lg:gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <h3 className="font-display text-2xl font-bold text-navy-900 mb-5">
                  Modern Community Hub Design
                </h3>
                <p className="text-gray-700 mb-4 leading-relaxed">
                  The new facility will feature contemporary architecture with extensive glazing,
                  creating a bright and welcoming environment for swimmers and the wider community.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Until the new facility opens, we continue to operate from Romiley and New Mills Pools,
                  maintaining our full range of programs and competitive training.
                </p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <img
                  src="/new-pool-exterior.jpg"
                  alt="New Community Hub Exterior Design"
                  className="w-full rounded-2xl shadow-xl transform hover:scale-[1.02] transition-transform duration-300"
                />
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <main className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* About Section */}
          <motion.section
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-16"
          >
            <h2 className="font-display text-3xl md:text-4xl font-bold text-navy-900 text-center mb-8">
              About Marple Swimming Club
            </h2>
            <div className="max-w-4xl mx-auto text-center space-y-4">
              <p className="text-lg text-gray-700 leading-relaxed">
                Our primary purpose is to train and maintain a competitive swimming team providing equal opportunity for all and an environment in which each swimmer can endeavour to realise their full potential. We cater for all age groups and abilities providing lessons for absolute beginner infants through to coaching sessions for senior competitive swimmers.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                If you are interested in joining the Club or would like any additional information or advice, please either come down to the Club on a Sunday afternoon or <Link to="/contact" className="text-primary-600 font-semibold hover:text-primary-700 underline">Contact us</Link>.
              </p>
            </div>
          </motion.section>

          {/* What We Offer */}
          <motion.section
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="mb-16"
          >
            <h2 className="font-display text-3xl md:text-4xl font-bold text-navy-900 text-center mb-12">
              What We Offer
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <motion.div
                variants={itemVariants}
                className="bg-white p-8 rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border-t-4 border-primary-600"
              >
                <h3 className="font-display text-2xl font-bold text-navy-900 mb-4">Competitive Training</h3>
                <p className="text-gray-700 leading-relaxed">
                  Professional coaching for swimmers looking to compete at local, regional, and national levels.
                </p>
              </motion.div>
              <motion.div
                variants={itemVariants}
                className="bg-white p-8 rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border-t-4 border-primary-600"
              >
                <h3 className="font-display text-2xl font-bold text-navy-900 mb-4">Learn to Swim</h3>
                <p className="text-gray-700 leading-relaxed">
                  Lessons for absolute beginner infants through to advanced stroke development for all ages.
                </p>
              </motion.div>
            </div>
          </motion.section>

          {/* Testimonials */}
          <motion.section
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-16"
          >
            <h2 className="font-display text-3xl md:text-4xl font-bold text-navy-900 text-center mb-12">
              What Parents Say
            </h2>
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Testimonial 1 */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="bg-white p-8 rounded-2xl shadow-md relative"
              >
                <div className="text-6xl text-primary-600 absolute top-4 left-4 opacity-10 font-display">"</div>
                <div className="relative z-10 space-y-4">
                  <p className="text-gray-700 leading-relaxed">
                    They have whole heartedly welcomed my 3 daughters into the club and nurtured their talents, taking time and care to build their confidence and encourage them to achieve their best in their competitive swimming. The coaches and young helpers at the club create an inclusive and friendly environment for all swimmers, regardless of ability.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    Two of my daughters are hearing impaired and the club go out of their way to accommodate their needs so that they can train and race on an even level with able swimmers. Countless children have benefitted from the swimming lessons and the opportunities for Marple youngsters to train, race and teach that the club provides.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    That the club have managed to keep going despite the lack of a Marple pool and through the challenges of Covid restrictions is a testament to the determination and tenacity of the small band of volunteers at the heart of the club. Marple is lucky to have such a wonderful group.
                  </p>
                  <p className="text-right font-bold text-navy-900 mt-6">
                    &mdash; Parent of Three Club Members
                  </p>
                </div>
              </motion.div>

              {/* Testimonial 2 */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.15 }}
                className="bg-white p-8 rounded-2xl shadow-md relative"
              >
                <div className="text-6xl text-primary-600 absolute top-4 left-4 opacity-10 font-display">"</div>
                <div className="relative z-10 space-y-4">
                  <p className="text-gray-700 leading-relaxed">
                    My Daughter is 16 and has been a member of Marple Swim Club for over 10 years. She was born with Down Syndrome but the swim club have always seen her as an individual and never a label. Many sports clubs will market themselves as 'inclusive' but as your child gets older and the gap between them and their peers widens it's difficult to find somewhere that can really adapt to your child's needs.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    This has NEVER been the case at Marple Swim Club. She has always been recognised as an active member of the club, taking part in swim galas and cheerleading the rest of the team. Recently she has started as a pool side assistant which she loves.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    Lucy and her team only see her for her abilities which is so important for her own personal development.
                  </p>
                  <p className="text-right font-bold text-navy-900 mt-6">
                    &mdash; Parent of 10-Year Club Member
                  </p>
                </div>
              </motion.div>
            </div>
          </motion.section>

          {/* Current Locations */}
          <motion.section
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <h2 className="font-display text-3xl md:text-4xl font-bold text-navy-900 text-center mb-12">
              Current Locations
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <motion.div
                variants={itemVariants}
                className="bg-white p-8 rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 text-center"
              >
                <h3 className="font-display text-xl font-bold text-navy-900 mb-4">Romiley Pool</h3>
                <p className="text-gray-700 leading-relaxed">
                  Training sessions and lessons available on Sunday afternoon for beginners all the way up to seniors
                </p>
              </motion.div>
              <motion.div
                variants={itemVariants}
                className="bg-white p-8 rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 text-center"
              >
                <h3 className="font-display text-xl font-bold text-navy-900 mb-4">New Mills Pool</h3>
                <p className="text-gray-700 leading-relaxed">
                  Wednesday evenings additional training venue while new pool is being built
                </p>
              </motion.div>
              <motion.div
                variants={itemVariants}
                className="bg-white p-8 rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 text-center"
              >
                <h3 className="font-display text-xl font-bold text-navy-900 mb-4">Visit Us</h3>
                <p className="text-gray-700 leading-relaxed">
                  Come down to the Club on a Sunday afternoon to learn more about joining
                </p>
              </motion.div>
            </div>
          </motion.section>
        </div>
      </main>
    </div>
  );
}

export default Home;
